import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import Phone from "./Phone";
import Address from "./Address";
import VendorTerms from "./VendorTerms";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { LOCATION_INFO } from "../constants";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import { cloneDeep, compact, isEmpty, uniq } from "lodash";
import { diff } from "deep-object-diff";

import { CONTACT_TYPES } from "./Contact";

export const COMPANY_FIELDS = {
  // Contact info
  name: "",
  type: "",
  position: [],
  emails: [],
  phones: [],
  addresses: [],

  // Members
  employees: [],
  admins: [],

  // Credit
  status: "",

  // Vendors
  vendorType: "",
  accountNumber: "",
  payableTo: "",
  taxID: "",
  is1099Eligible: false,
  isCashAccount: false,
  paymentTerms: null,
  vendorDocs: [],

  // Material Vendors
  series: [],

  // Customers
  holdReason: "",
  creditApproved: false,
  creditApp: null,
  creditLimit: null,
  creditReferences: [],
  w9: null,
  isWholesale: false,
  // Wholesale accounts name the location their sales are assigned to
  // (Justin, 2026-08-23); quotes for the account default there.
  location: "",

  // Requests: a customer company any employee asked for, which no one can
  // send a quote to until accounting approves it (clears `requested`).
  // `possibleDuplicates` are the strong matches the requester marked "Not a
  // duplicate": `{ id, name, on }`, `on` naming what they share.
  requested: false,
  possibleDuplicates: [],

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(COMPANY_FIELDS);

export const COMPANY_SEARCHABLE_FIELDS = [
  "id",
  // Company name
  "name",
  "data.name",
  // Displayed type / vendor type / status labels
  "type.label",
  "vendorType.label",
  "status.label",
  // Account identifiers
  "accountNumber",
  "data.accountNumber",
  "payableTo",
  "data.payableTo",
  "taxID",
  "data.taxID",
  // Customer-specific fields
  "holdReason",
  "data.holdReason",
];
deepFreeze(COMPANY_SEARCHABLE_FIELDS);

export const COMPANY_ACCOUNT_STATUS_TYPES = {
  inactive: {
    order: 0,
    label: "Cash/no terms",
    icon: "cash",
    color: "negative",
  },
  pending: {
    order: 1,
    label: "Pending",
    icon: "pending",
    color: "info",
  },
  hold: {
    order: 2,
    label: "On hold",
    icon: "warning",
    color: "warning",
  },
  active: {
    order: 3,
    label: "Active",
    icon: "qualified",
    color: "positive",
  },
};
deepFreeze(COMPANY_ACCOUNT_STATUS_TYPES);

// How a request accounting hasn't approved shows in company lists, in place
// of its account status
export const COMPANY_REQUESTED_STATUS = {
  order: -1,
  label: "Requested",
  icon: "review_pending",
  color: "warning",
};
deepFreeze(COMPANY_REQUESTED_STATUS);

export const COMPANY_VENDOR_TYPES = {
  expense: {
    label: "Expense",
    icon: "sale",
  },
  material: {
    label: "Material",
    icon: "window",
  },
  labor: {
    label: "Labor",
    icon: "service",
  },
};
deepFreeze(COMPANY_VENDOR_TYPES);

export default class Company extends DataStructure {
  constructor(id, data = {}) {
    data = Company.sanitize(data);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    // Account Status
    if (!data.status) {
      data.status = "inactive";
    }

    // Vendor Info
    if (data.type === "vendor") {
      if (!data.vendorType) {
        data.vendorType = "expense";
      }
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Company.canBeViewedBy,
      create: Company.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });

    // Vendor Docs
    if (data.vendorDocs) {
      this.data.vendorDocs = this.vendorDocs.map((d) => d.data);
    }

    // Credit App
    if (this.data.creditApp) {
      this.data.creditApp = this.creditApp.data;
    }

    // Credit References
    if (data.creditReferences) {
      this.data.creditReferences = this.creditReferences.map((r) => r.data);
    }

    // W9
    if (this.data.w9) {
      this.data.w9 = this.w9.data;
    }
  }

  // Permissions

  isAdmin(user) {
    return this.data.admins.includes(user.id);
  }

  isEmployee(user) {
    return this.data.employees.includes(user.id);
  }

  static canBeViewedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("accounting"));
  }

  static canBeCreatedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("accounting"));
  }

  // Anyone on staff can ask for a customer company; accounting approves it
  static canBeRequestedBy(user) {
    return Boolean(user && user.isEmployee);
  }

  static canBeApprovedBy(user) {
    return Company.canBeCreatedBy(user);
  }

  canBeEditedBy(user) {
    return (
      user.isAdmin ||
      (user.isEmployee && user.hasRole("accounting")) ||
      this.isAdmin(user) ||
      this.isRequestedBy(user)
    );
  }

  // The requester can change their request until it's approved
  isRequestedBy(user) {
    return Boolean(
      user &&
        user.isEmployee &&
        this.data.requested &&
        this.data.createdBy === user.id
    );
  }

  canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Company.getPath(this.id);
  }

  static getPath(id) {
    let path = `companies`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(company) {
    return company.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Company(snapshot.id, data);
  }

  // Editing

  static isNameValid(name) {
    return Boolean(name && name.trim().length > 0);
  }

  static isTypeValid(type, isVendor) {
    return Boolean(
      type &&
        (isVendor
          ? type in COMPANY_VENDOR_TYPES
          : type in CONTACT_TYPES && !["employee", "vendor"].includes(type))
    );
  }

  get termsAreValid() {
    return (
      !this.isVendor ||
      this.data.isCashAccount === true ||
      (this.paymentTerms && this.paymentTerms.isValid)
    );
  }

  get isValid() {
    if (
      !(
        Company.isNameValid(this.data.name) &&
        this.emails.every((email) => email.isValid) &&
        Company.isTypeValid(
          this.isVendor ? this.data.vendorType : this.data.type,
          this.isVendor
        )
      )
    ) {
      return false;
    }

    // A wholesale account must name the location its sales are assigned to.
    if (this.isWholesale && !this.data.location) {
      return false;
    }

    return this.termsAreValid;
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, COMPANY_FIELDS)));
  }

  isEqual(company) {
    return Boolean(
      this.id === company.id &&
        isEmpty(
          diff(
            Company.sanitize(cloneDeep(this.data)),
            Company.sanitize(cloneDeep(company.data))
          )
        )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.creditApp) {
      const doc = new DocumentRef(data.creditApp);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.creditApp = null;
      }
    }
    if (data.creditReferences) {
      data.creditReferences = data.creditReferences.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    if (data.w9) {
      const doc = new DocumentRef(data.w9);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.w9 = null;
      }
    }
    if (data.vendorDocs) {
      data.vendorDocs = data.vendorDocs.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    return data;
  }

  static sanitize(data) {
    // Trim
    if (data.name) {
      data.name = data.name.trim();
    }
    if (data.accountNumber) {
      data.accountNumber = data.accountNumber.trim();
    }
    if (data.payableTo) {
      data.payableTo = data.payableTo.trim();
    }
    if (data.taxID) {
      data.taxID = data.taxID.trim();
    }
    if (data.holdReason) {
      data.holdReason = data.holdReason.trim();
    }

    // Clone and conform to data structure
    data = sanitize(data, COMPANY_FIELDS);

    // Clean up documents
    Company.cleanDocuments(data);

    // Clean up conditional fields
    if (data.type === "vendor") {
      // Vendor
      if (data.vendorType === "material") {
        data.series = uniq(compact(data.series)).sort();
      } else {
        data.series = [];
      }
      if (data.isCashAccount) {
        data.paymentTerms = null;
      } else if (data.paymentTerms && data.paymentTerms.label) {
        data.paymentTerms.label = data.paymentTerms.label.trim();
      }
    } else {
      // Customer
      if (data.status !== "hold") {
        data.holdReason = "";
      }
      data.isWholesale = Boolean(data.isWholesale);
    }

    return data;
  }

  // Formatting

  get hasAccountInfo() {
    return (
      this.data.accountNumber ||
      this.data.creditLimit ||
      this.data.payableTo ||
      this.data.taxID ||
      this.data.is1099Eligible ||
      this.data.isCashAccount ||
      this.data.paymentTerms ||
      this.docs.length > 0
    );
  }

  get contact() {
    return this.id ? store.state.main.contacts[this.id] || null : null;
  }

  get isVendor() {
    return this.data.type === "vendor";
  }

  get isWholesale() {
    return !this.isVendor && Boolean(this.data.isWholesale);
  }

  // The location a wholesale account's sales are assigned to, as a
  // LOCATION_INFO entry for CompanyLocationBadge. `|| null` so an unrecognized
  // key degrades to null rather than undefined, which trips the badge's
  // required-Object prop.
  get location() {
    return this.data.location
      ? LOCATION_INFO[this.data.location] || null
      : null;
  }

  get type() {
    return CONTACT_TYPES[this.data.type] || CONTACT_TYPES.other;
  }

  get icon() {
    return this.type.icon;
  }

  get vendorType() {
    return COMPANY_VENDOR_TYPES[this.data.vendorType];
  }

  get emails() {
    return this.data.emails.map((email) => new Email(email));
  }

  get phones() {
    return this.data.phones.map((phone) => new Phone(phone));
  }

  get addresses() {
    return this.data.addresses.map((address) => new Address(address));
  }

  get employees() {
    return this.data.employees
      .map((uid) => store.state.main.contacts[uid])
      .filter((c) => c);
  }

  get admins() {
    return this.data.admins
      .map((uid) => store.state.main.contacts[uid])
      .filter((c) => c);
  }

  get creditApp() {
    return this.data.creditApp ? new DocumentRef(this.data.creditApp) : null;
  }

  get creditReferences() {
    return this.data.creditReferences
      ? this.data.creditReferences.map((d) => new DocumentRef(d))
      : [];
  }

  get w9() {
    return this.data.w9 ? new DocumentRef(this.data.w9) : null;
  }

  get vendorDocs() {
    return this.data.vendorDocs
      ? this.data.vendorDocs.map((d) => new DocumentRef(d))
      : [];
  }

  get docs() {
    return compact([
      this.creditApp,
      ...this.creditReferences,
      this.w9,
      ...this.vendorDocs,
    ]);
  }

  get creditLimit() {
    return formatCurrency(this.data.creditLimit);
  }

  get paymentTerms() {
    return this.data.paymentTerms
      ? new VendorTerms(this.data.paymentTerms)
      : null;
  }

  get status() {
    const type = COMPANY_ACCOUNT_STATUS_TYPES[this.data.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  // The status a list shows: Requested until accounting approves it
  get listStatus() {
    return this.data.requested
      ? {
          ...COMPANY_REQUESTED_STATUS,
          ...COLORS[COMPANY_REQUESTED_STATUS.color],
        }
      : this.status;
  }

  get balance() {
    const balance = store.state.main.balances[this.id];
    return balance ? balance.total : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name;
  }
}
